import { Canvas, useFrame } from "@react-three/fiber";
import { CameraControls, Grid } from "@react-three/drei";
import { EffectComposer, Outline } from "@react-three/postprocessing";
import { useRef } from "react";
import { useParams } from "react-router-dom";
import { DEG2RAD } from "three/src/math/MathUtils.js";
import {
  useModelStore,
  usePlacedModelsStore,
  useUserInterfaceStore,
} from "../../store";
import { useShallow } from "zustand/shallow";
import GLBObjectList from "./GlbObjectList";
import PlacedAvatars from "./PlacedAvatars";

import CustomTransformControls from "./CustomTransformControls";
import FixedElements from "./FixedElements";
import RoomEditor from "./RoomEditor";
import { playAllPendingVideos } from "./VideoMaterial";
import Decorations from "./Decorations";
import Door from "./Door";
import RandomDoor from "./RandomDoor";
import ThumbnailCapture from "../ThumbnailCapture";
import RendererRegistry from "../RendererRegistry";
import { BlendFunction } from "postprocessing";

/**
 * Auto-rotate camera component for step 4 (publish preview)
 */
function AutoRotateCamera({ cameraControls, rotationSpeed = 15 }) {
  useFrame((_, delta) => {
    if (cameraControls) {
      cameraControls.azimuthAngle += rotationSpeed * delta * DEG2RAD;
    }
  });
  return null;
}

function EditorScene() {
  const { roomId } = useParams();
  const cameraControlsRef = useRef();
  const setCameraControls = useModelStore((s) => s.setCameraControls);
  const selectedMeshes = useModelStore((s) => s.selectedMeshes);
  const models = usePlacedModelsStore(useShallow((s) => s?.placedModels || []));
  const editingRoom = useUserInterfaceStore((s) => s.editingRoom);
  const lockCameraForEditor = useUserInterfaceStore(
    (s) => s.lockCameraForEditor
  );
  const autoRotateCamera = useUserInterfaceStore((s) => s.autoRotateCamera);
  const cameraControls = useModelStore((s) => s.cameraControls);
  const selectedMeshesForOutline = Array.from(
    new Set(
      selectedMeshes.flatMap((mesh) => {
        let groupRoot = mesh;
        while (groupRoot && !groupRoot.userData?.isGroupRoot) {
          groupRoot = groupRoot.parent;
        }
        const meshes = [];
        (groupRoot || mesh).traverse?.((child) => {
          if (child.isMesh) meshes.push(child);
        });
        return meshes;
      })
    )
  );

  return (
    <Canvas
      shadows
      camera={{ position: [70, 70, 70], near: 0.1, far: 10000 }}
      style={{ position: "relative", zIndex: 1, touchAction: "none" }}
      onPointerDown={playAllPendingVideos}
    >
      <CameraControls
        ref={(ref) => {
          if (!cameraControlsRef.current) {
            cameraControlsRef.current = ref;
            setCameraControls(cameraControlsRef.current);
          }
        }}
        enabled={!lockCameraForEditor}
        makeDefault
      />
      {autoRotateCamera && <AutoRotateCamera cameraControls={cameraControls} />}
      <CustomTransformControls selectedMeshes={selectedMeshes} />
      <GLBObjectList models={models} />
      <PlacedAvatars />
      <RoomEditor />
      <Door />
      <RandomDoor />
      {!editingRoom && <Decorations />}
      <ThumbnailCapture roomId={roomId} enabled={true} />
      <RendererRegistry />
      <Grid
        args={[250, 250]}
        cellSize={5}
        cellThickness={1}
        cellColor="#222"
        sectionSize={10}
        sectionThickness={1.5}
        sectionColor="#444"
        fadeDistance={10000}
        fadeStrength={0}
        side={2}
      />
      <EffectComposer autoClear={false}>
        <Outline
          selection={selectedMeshesForOutline}
          edgeStrength={50}
          visibleEdgeColor={0xff0000}
          hiddenEdgeColor={0xff0000}
          xRay={true}
          blendFunction={BlendFunction.ALPHA}
        />
      </EffectComposer>
      <FixedElements isEditing={!editingRoom} />
    </Canvas>
  );
}

export default EditorScene;
